import { defineStore } from 'pinia'
import useModalStore from './modal'
import * as Sentry from "@sentry/vue"

// Error codes and messages
const errorCodeMapping = {
  // Campaign-related errors
  'CAMPAIGN_ENDED': 'This campaign has already ended. You can no longer participate.',
  'CAMPAIGN_LIMIT_REACHED': 'This campaign has reached its maximum number of participants.',
  
  // User eligibility errors
  'DAILY_LIMIT_EXCEEDED': 'You have reached the daily participation limit for this campaign.',
};

// User-friendly messages for HTTP status codes
const statusCodeMessages = {
  400: 'Please log in to continue. Your session may have expired.',
  401: 'Please log in to access this feature. Authentication is required.',
  403: 'You do not have permission to access this feature.',
  404: 'The requested resource was not found.',
  500: 'Something went wrong on our end. Please try again later.',
  502: 'Service temporarily unavailable. Please try again later.',
  503: 'Service temporarily unavailable. Please try again later.',
};


export default defineStore('error', {
  state: () => ({
    errorStack: []
  }),
  getters: {},
  actions: {
    handleError(error) {
      // Log error handling start to Sentry only
      this.logToSentryOnly('Error handling initiated', 'info', 'error_store', {
        errorType: error.constructor.name,
        hasResponse: !!error.response,
        hasRequest: !!error.request
      });
      
      this.errorStack.push(error);
      
      const modalStore = useModalStore();
      let type = 'error'; // default to error
      let message = 'An unknown error occurred';
      
      // Set Sentry context for better error tracking
      Sentry.withScope((scope) => {
        if (error.response) {
          const status = error.response.status;
          const apiResponse = error.response.data;

          // Capture backend message once so we can use it everywhere
          const backendErrorMessage = apiResponse?.message || apiResponse?.error || error.message;

          // Log API response error to Sentry only
          this.logToSentryOnly(`API Error - Status: ${status}`, 'error', 'api_error', {
            status,
            url: error.response.config?.url,
            method: error.response.config?.method,
            errorCode: apiResponse?.errorCode,
            backendMessage: backendErrorMessage
          });

          // ✅ Add tags (searchable in Sentry)
          scope.setTag("error_type", "api_response");
          scope.setTag("status_code", status);
          if (backendErrorMessage) {
            scope.setTag("backendMessage", backendErrorMessage); // <-- searchable!
          }

          // Add context (visible in UI but not searchable)
          scope.setContext("api_response", {
            status: status,
            data: apiResponse,
            url: error.response.config?.url,
            method: error.response.config?.method,
            requestPayload: this.extractRequestPayload(error.response.config)
          });

          // Check if we have an error code mapping first
          const errorCodeMessage = apiResponse.errorCode ? errorCodeMapping[apiResponse.errorCode] : null;
          
          // If we have a mapped error code, use it; otherwise use backend message or fallback to status message
          if (errorCodeMessage) {
            message = errorCodeMessage;
          } else {
            // No error code mapping, use backend message or fallback to generic status message
            message = backendErrorMessage || statusCodeMessages[status] || 'An unknown error occurred';
          }

          switch (status) {
            case 400:
              type = 'warning';
              scope.setLevel("warning");
              break;
            case 401:
              type = 'error';
              scope.setLevel("error");
              break;
            case 500:
              type = 'error';
              scope.setLevel("error");
              break;
            default:
              type = 'error';
              scope.setLevel("error");
          }

          // Log Sentry capture event to Sentry only
          this.logToSentryOnly('Sending error to Sentry', 'info', 'sentry', {
            sentryMessage: backendErrorMessage,
            statusCode: status,
            errorType: type
          });

          // Capture the error with Sentry using backend message if available
          Sentry.captureMessage(backendErrorMessage);
          
        } else if (error.request) {
          type = 'error';
          message = 'No response from server';
          
          // Log network error to Sentry only
          this.logToSentryOnly('Network Error - No response received', 'error', 'network_error', {
            url: error.request.responseURL || error.config?.url,
            method: error.config?.method,
            timeout: error.config?.timeout
          });
          
          // Set context for network errors
          scope.setTag("error_type", "network");
          scope.setContext("request", {
            url: error.request.responseURL || error.config?.url,
            method: error.config?.method,
            timeout: error.config?.timeout
          });
          scope.setLevel("error");
          
          // Capture the error with Sentry using descriptive message
          Sentry.captureMessage(`Network error: ${message}`);
          
        } else {
          // For other types of errors (like JavaScript errors or custom errors)
          scope.setTag("error_type", "unknown");
          scope.setLevel("error");
          
          // Log unknown error to Sentry only
          this.logToSentryOnly('Unknown Error Type', 'error', 'unknown_error', {
            errorMessage: error.message,
            errorName: error.name,
            hasEndpoint: !!error.endpoint,
            hasRequestPayload: !!error.requestPayload,
            hasResponseData: !!error.responseData
          });
          
          // Handle custom error objects with additional context
          if (error.endpoint) {
            scope.setTag("endpoint", error.endpoint);
          }
          if (error.requestPayload) {
            scope.setContext("request_payload", error.requestPayload);
          }
          if (error.responseData) {
            scope.setContext("response_data", error.responseData);
          }
          if (error.retryCount !== undefined) {
            scope.setTag("retry_count", error.retryCount);
          }
          
          // Capture the error with Sentry using error message or fallback
          const errorMessage = error.message || 'Unknown error occurred';
          Sentry.captureMessage(errorMessage);
        }
      });

      modalStore.openModal(type, message);
    },
    
    // Helper method for logging to Sentry with breadcrumbs (visible only in Sentry)
    logToSentryOnly(message, level = 'info', category = 'custom', data = {}) {
      // Add breadcrumb (this will be visible in Sentry breadcrumbs)
      Sentry.addBreadcrumb({
        message,
        category,
        level,
        data: {
          timestamp: new Date().toISOString(),
          source: 'error_store',
          ...data
        }
      });
      
      // Also capture as message if it's important enough
      if (level === 'error' || level === 'warning') {
        Sentry.withScope((scope) => {
          scope.setLevel(level);
          scope.setTag("source", "error_store_log");
          scope.setTag("category", category);
          scope.setContext("log_data", data);
          
          Sentry.captureMessage(message, level);
        });
      }
    },
    
    // Helper method to extract request payload for debugging
    extractRequestPayload(config) {
      if (!config) return null;
      
      let payload = {};
      
      // Extract form data if present
      if (config.data instanceof FormData) {
        payload.type = 'FormData';
        payload.entries = [];
        try {
          for (let [key, value] of config.data.entries()) {
            if (value instanceof File) {
              payload.entries.push({
                key,
                type: 'File',
                name: value.name,
                size: value.size,
                lastModified: value.lastModified
              });
            } else {
              payload.entries.push({ key, value });
            }
          }
        } catch (e) {
          payload.error = 'Could not extract FormData entries';
        }
      } else if (typeof config.data === 'string') {
        try {
          payload = JSON.parse(config.data);
        } catch (e) {
          payload = { rawData: config.data };
        }
      } else if (config.data) {
        payload = config.data;
      }
      
      return payload;
    },
    clearError() {
      this.errorStack = [];
    },
    
    // Manual logging methods for custom errors or debugging
    logToSentry(message, level = 'info', extra = {}) {
      Sentry.withScope((scope) => {
        scope.setLevel(level);
        scope.setTag("source", "manual_log");
        scope.setContext("extra_data", extra);
        
        if (level === 'error') {
          Sentry.captureException(new Error(message));
        } else {
          Sentry.captureMessage(message, level);
        }
      });
    },
    
    // Log user actions or business logic events
    logUserAction(action, data = {}) {
      Sentry.withScope((scope) => {
        scope.setTag("event_type", "user_action");
        scope.setTag("action", action);
        scope.setContext("action_data", data);
        scope.setLevel("info");
        
        Sentry.captureMessage(`User action: ${action}`, "info");
      });
    }
  }
})
